import { useEffect, useMemo, useState } from 'react'
import { useAuth, useSocket, useWorkspace } from '@/context'
import type { ResourcePresenceViewer, WorkspacePresenceUpdateEvent } from '@/types/websocket'
import { excludeSelf } from '@/utils/presence'
import { getPresenceDisplayName } from '@/utils/userProfile'

export function useWorkspacePresence(workspaceId?: string | null): ResourcePresenceViewer[] {
	const { socket, connected } = useSocket()
	const { user } = useAuth()
	const { selectedWorkspaceId } = useWorkspace()
	const effectiveWorkspaceId = workspaceId ?? selectedWorkspaceId
	const [viewers, setViewers] = useState<ResourcePresenceViewer[]>([])

	useEffect(() => {
		if (!socket || !connected || !effectiveWorkspaceId) {
			setViewers([])
			return
		}

		const handlePresenceUpdate = (data: WorkspacePresenceUpdateEvent) => {
			if (String(data.workspace_id) !== String(effectiveWorkspaceId)) return
			setViewers(data.viewers ?? [])
		}

		socket.on('workspace:presence:update', handlePresenceUpdate)

		if (user?.id) {
			socket.emit('subscribe_workspace', {
				workspace_id: effectiveWorkspaceId,
				display_name: getPresenceDisplayName(user),
			})
		}

		return () => {
			socket.off('workspace:presence:update', handlePresenceUpdate)
		}
	}, [socket, connected, effectiveWorkspaceId, user])

	return useMemo(() => excludeSelf(viewers, user?.id), [viewers, user?.id])
}
